/**
 * The invite link's page (public route `/accept-invite`, Phase 7b): the token
 * comes from the URL FRAGMENT (`#token=…`, never sent to a server by the
 * browser), is checked (`POST /invites/inspect`), and the new password is set
 * (`POST /invites/accept`) — both without a token of our own. Then on to the
 * normal sign-in.
 */
import { ApiError } from '@norna/api-client/client.js';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useAcceptInvite, useInspectInvite, useServices } from '../app/hooks.js';
import { AcceptInviteForm, type AcceptInviteState } from '../components/AcceptInviteForm.js';
import { inviteTokenFromHash } from '../lib/admin/inviteLink.js';
import { messageOf } from './admin/common.js';

const INVALID = 'This invite link is not valid: it may be mistyped, used, replaced by a newer one or expired. Ask your administrator to resend it.';

export function AcceptInviteView() {
  const navigate = useNavigate();
  const [token] = useState(() => inviteTokenFromHash(typeof window === 'undefined' ? '' : window.location.hash));
  // A studio server serves no invites (`RouteDef.hosts`): the link is the cloud portal's, so nothing is asked here.
  const served = useServices().config.backend.kind === 'cloud';
  const invite = useInspectInvite(served ? token : null);
  const accept = useAcceptInvite();
  const [done, setDone] = useState<string | null>(null);

  let state: AcceptInviteState = { kind: 'loading' };
  if (done !== null) state = { kind: 'done', email: done };
  else if (token === null) state = { kind: 'invalid', message: 'This page opens from the link in your invite email.' };
  else if (!served) state = { kind: 'invalid', message: 'Invites are accepted in the cloud portal, not on a studio server: open the link in your invite email as it came.' };
  else if (invite.error !== null) state = { kind: 'invalid', message: invite.error instanceof ApiError && invite.error.status === 401 ? INVALID : messageOf(invite.error) };
  else if (invite.data !== undefined) state = { kind: 'ready', ...invite.data };

  return (
    <main className="flex min-h-svh items-center justify-center bg-background p-4">
      <AcceptInviteForm
        state={state}
        pending={accept.isPending}
        error={accept.error === null ? null : accept.error instanceof ApiError && accept.error.status === 401 ? INVALID : messageOf(accept.error)}
        onSubmit={(password) => {
          if (token !== null) accept.mutate({ token, password }, { onSuccess: (result) => setDone(result.email) });
        }}
        onSignIn={() => {
          void navigate({ to: '/login' });
        }}
      />
    </main>
  );
}
